require('./RAEvolutionChart.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} = require('recharts');
const classnames = require('classnames');
const { isEmpty } = require('lodash');

// @components
const RAEvolutionChartTooltip = require('./RAEvolutionChartTooltip');
const RACustomTickWithObjective = require('./RACustomTickWithObjective/RACustomTickWithObjective');
const RACustomizedCursorLine = require('../RACustomizedCursorLine/RACustomizedCursorLine');

//@utilities
const { getFormattedString } = require('utilities/currencyHelper');
const { formatText } = require('core/i18n').i18n;
const {
    getShowPonitsAndDayTerm,
} = require('../../utilities/RAObjectiveTrackingGetYearAndNoOfPoints');

// @constants
const { EURO_SYMBOL, EURO_TEXT } = require('constants/index');
const { DUMMY_DATA, DUMMY_OBJECTIVE_VALUE } = require('./constants');
const {
    LINE_LABEL_COLOR,
    REF_LINE_COLOR,
    REF_DOT_FILL_COLOR,
    REF_DOT_STROKE_COLOR,
    AXIS_LINE_COLOR,
    TICK_DISABLED_COLOR,
    TICK_TEXT_COLOR,
} = require('../../raConstants/RAObjectiveTracking');

const VALUATION_KEY = 'valuation';
const VALUATION_FILTER = EURO_TEXT;
const PERCENT_KEY = 'percentValue';
const Y_AXIS_INTERVALS = 5;

class RAEvolutionChart extends React.Component {
    constructor(props) {
        super(props);
        this.YAxisFormatter = this.YAxisFormatter.bind(this);
        this.getLineShape = this.getLineShape.bind(this);
        this.getActiveDot = this.getActiveDot.bind(this);
    }

    isValuation() {
        const { selectedOption } = this.props;
        return selectedOption === VALUATION_FILTER;
    }

    YAxisFormatter(tick) {
        const { disabled, hasRoboNotHired } = this.props;
        if (disabled && !hasRoboNotHired) {
            return '';
        }

        return this.isValuation()
            ? this.moneyFormatter(tick)
            : this.percentFormatter(tick);
    }

    percentFormatter(tick) {
        return `${tick.toFixed(2)}%`;
    }

    moneyFormatter(tick) {
        const str = getFormattedString({ amount: tick, currency: EURO_SYMBOL });
        return str;
    }

    buildTooltip() {
        const tooltip = (
            <Tooltip
                content={
                    <RAEvolutionChartTooltip
                        selectedOption={this.props.selectedOption}
                        isValuation={this.isValuation()}
                    />
                }
                cursor={<RACustomizedCursorLine />}
            />
        );
        return tooltip;
    }

    getLineShape({ x1, y1, x2, y2 }) {
        const {
            goalProgress,
            objAmount,
            isObjectiveTracking,
            disabled,
            hasRoboNotHired,
        } = this.props;
        let shape;
        let rectYvalue, textYvalue;
        if (y1 > 200) {
            rectYvalue = y1 - 30;
            textYvalue = y1 - 15;
        } else {
            rectYvalue = y1 + 10;
            textYvalue = y1 + 25;
        }
        if (
            (disabled && !hasRoboNotHired) ||
            (isObjectiveTracking && goalProgress >= 0.9)
        ) {
            const objectiveAmount = getFormattedString({
                amount: objAmount,
                currency: EURO_SYMBOL,
            });
            const objectiveLabel = formatText(
                'raObjectiveTracking-raObjectiveTracking_evolutionLineLabel',
                [objectiveAmount],
            );
            const LINE_COLOR = disabled ? TICK_DISABLED_COLOR : REF_LINE_COLOR;
            const lineLabelElement = !disabled && (
                <g>
                    <rect
                        x={x1 + 10}
                        y={rectYvalue}
                        width="170"
                        height="20"
                        fill={LINE_LABEL_COLOR}
                    />
                    <text
                        x={x1 + 95}
                        y={textYvalue}
                        fill={REF_DOT_STROKE_COLOR}
                        textAnchor="middle"
                        className="ra-evolution-chart__objective-label"
                    >
                        {objectiveLabel.toUpperCase()}
                    </text>
                </g>
            );
            shape = (
                <g>
                    <defs>
                        <marker
                            id="arrow1"
                            markerWidth="20"
                            markerHeight="20"
                            refX="0"
                            refY="3"
                            orient="auto"
                            markerUnits="strokeWidth"
                            viewBox="0 0 20 20"
                        >
                            <path d="M0,0 L0,6 L9,3 z" fill={LINE_COLOR}></path>
                        </marker>
                        <marker
                            id="arrow2"
                            markerWidth="20"
                            markerHeight="20"
                            refX="0"
                            refY="3"
                            orient="180deg"
                            markerUnits="strokeWidth"
                            viewBox="0 0 20 20"
                        >
                            <path d="M0,0 L0,6 L9,3 z" fill={LINE_COLOR}></path>
                        </marker>
                    </defs>
                    <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={LINE_COLOR}
                        strokeWidth="1"
                        markerStart="url(#arrow1)"
                        markerEnd="url(#arrow2)"
                    />
                    {lineLabelElement}
                </g>
            );
        }
        return shape;
    }

    getActiveDot({ cx, cy }) {
        return (
            <g>
                <defs>
                    <filter
                        id="dropShadow"
                        x="-40%"
                        y="-40%"
                        width="180%"
                        height="180%"
                        filterRes="1200"
                        filterUnits="userSpaceOnUse"
                    >
                        <feGaussianBlur
                            in="SourceAlpha"
                            stdDeviation="1"
                            result="blur"
                        />
                        <feOffset dx="0" dy="1" in="blur" result="offset" />
                        <feComponentTransfer>
                            <feFuncA
                                type="linear"
                                slope="0.5"
                                in="offset"
                                result="transfer"
                            />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <circle
                    cx={cx}
                    cy={cy}
                    strokeWidth="3"
                    r="10"
                    stroke={REF_DOT_STROKE_COLOR}
                    fill={REF_DOT_FILL_COLOR}
                    filter="url(#dropShadow)"
                />
            </g>
        );
    }

    createTicksArray(data, currentKey, objAmount) {
        const { goalProgress } = this.props;
        const maxValue = data.reduce((maxValueObject, valueObject) => {
            return maxValueObject[currentKey] > valueObject[currentKey]
                ? maxValueObject
                : valueObject;
        })[currentKey];

        const minValue = data.reduce((maxValueObject, valueObject) => {
            return maxValueObject[currentKey] < valueObject[currentKey]
                ? maxValueObject
                : valueObject;
        })[currentKey];

        let term;
        if (currentKey === VALUATION_KEY) {
            const max =
                goalProgress >= 0.9 ? Math.max(maxValue, objAmount) : maxValue;
            term = max - minValue;
            term = Math.ceil(term / Y_AXIS_INTERVALS);
        } else {
            term = maxValue - minValue;
            term = Math.ceil((term / Y_AXIS_INTERVALS) * 100) / 100;
        }

        const initialTicksArray = new Array(Y_AXIS_INTERVALS + 1).fill(
            minValue,
        );
        const resultTicksArray = initialTicksArray.map((value, index) => {
            const tickValue =
                currentKey === VALUATION_KEY
                    ? Math.ceil(value) + term * index
                    : value + term * index;

            return tickValue;
        });

        return resultTicksArray;
    }

    render() {
        const { data, disabled, objAmount, hasRoboNotHired } = this.props;
        const tooltip = disabled ? <span /> : this.buildTooltip();
        const currentKey = this.isValuation() ? VALUATION_KEY : PERCENT_KEY;
        const currentData = disabled || isEmpty(data) ? DUMMY_DATA : data;
        const currentClass = classnames('ra-evolution-chart', {
            'ra-evolution-chart--disabled': disabled,
        });
        const noOfChartPoints = currentData.length;
        const showPointsList = getShowPonitsAndDayTerm(noOfChartPoints);
        const customTickELement = (
            <RACustomTickWithObjective
                noOfPoints={noOfChartPoints}
                showPointsList={showPointsList}
                disabled={disabled}
                hasRoboNotHired={hasRoboNotHired}
            />
        );
        const referenceLineValue =
            disabled && !hasRoboNotHired ? DUMMY_OBJECTIVE_VALUE : objAmount;
        const ticksArray =
            __CONTACT_CENTER__ && currentData === DUMMY_DATA
                ? null
                : this.createTicksArray(currentData, currentKey, objAmount);
        const graph = (
            <div className={currentClass}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={currentData}
                        margin={{ top: 20, right: 30 }}
                    >
                        <CartesianGrid vertical={false} strokeDasharray="2" />
                        <YAxis
                            width={120}
                            interval="preserveStartEnd"
                            tickLine={false}
                            dataKey={currentKey}
                            tick={
                                hasRoboNotHired
                                    ? { fill: TICK_DISABLED_COLOR }
                                    : { fill: TICK_TEXT_COLOR }
                            }
                            tickFormatter={this.YAxisFormatter}
                            tickMargin={10}
                            axisLine={false}
                            ticks={ticksArray}
                            domain={['dataMin', 'dataMax']}
                        />
                        <XAxis
                            height={50}
                            tickLine={false}
                            dataKey="bookingDate"
                            tick={customTickELement}
                            tickFormatterX={this.timeFormatter}
                            tickMargin={10}
                            scale="point"
                            interval={0}
                            axisLine={{
                                stroke: AXIS_LINE_COLOR,
                                strokeWidth: 1,
                            }}
                        />
                        {tooltip}
                        <Line
                            type="monotoneX"
                            dataKey={currentKey}
                            className="ra-evolution-chart__line"
                            stroke=""
                            strokeWidth={2}
                            dot={{ opacity: 0 }}
                            activeDot={this.getActiveDot}
                        />
                        <Area
                            type="monotoneX"
                            dataKey={currentKey}
                            className="ra-evolution-chart__area"
                            fill=""
                            stroke=""
                            dot={false}
                            activeDot={false}
                        />
                        <ReferenceLine
                            y={referenceLineValue}
                            shape={this.getLineShape}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );

        return graph;
    }
}

RAEvolutionChart.propTypes = {
    data: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    isObjectiveTracking: PropTypes.bool,
    goalProgress: PropTypes.number,
    objAmount: PropTypes.number,
    selectedOption: PropTypes.string.isRequired,
    hasRoboNotHired: PropTypes.bool,
};

RAEvolutionChart.defaultProps = {
    isObjectiveTracking: false,
    objAmount: 0,
    hasRoboNotHired: false,
};
module.exports = RAEvolutionChart;
